import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Snackbar,
  Autocomplete,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  GetApp as DownloadIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { InvoicesService, ClientsService, Invoice, Client, CreateInvoiceData, FinancialStats, UnbilledHoursData } from '../../../services/api';

const InvoicesPage: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [stats, setStats] = useState<FinancialStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [formData, setFormData] = useState<Partial<CreateInvoiceData>>({
    status: 'draft',
    issue_date: new Date().toISOString().split('T')[0],
  });
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [unbilledHours, setUnbilledHours] = useState<UnbilledHoursData | null>(null);
  const [loadingUnbilled, setLoadingUnbilled] = useState(false);
  const [showUnbilledDetails, setShowUnbilledDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    loadInvoices();
    loadStats();
    loadClients();
  }, [page, rowsPerPage, statusFilter]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const response = await InvoicesService.getInvoices({
        page: page + 1,
        per_page: rowsPerPage,
        status: statusFilter !== 'all' ? statusFilter : undefined,
      });
      setInvoices(response.invoices);
      setTotalInvoices(response.pagination.total);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await InvoicesService.getStats();
      setStats(statsData);
    } catch (err: any) {
      console.error('Failed to load stats:', err);
    }
  };

  const loadClients = async () => {
    try {
      const response = await ClientsService.getClients({ status: 'active' });
      setClients(response.clients);
    } catch (err: any) {
      console.error('Failed to load clients:', err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'success';
      case 'sent': return 'info';
      case 'overdue': return 'error';
      case 'draft': return 'default';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const loadUnbilledHours = async (clientId: number) => {
    try {
      setLoadingUnbilled(true);
      const data = await InvoicesService.getUnbilledHours(clientId);
      setUnbilledHours(data);
      
      // Auto-populate amount with total unbilled amount
      if (data.totalAmount > 0) {
        setFormData(prev => ({
          ...prev,
          amount: data.totalAmount,
          description: `Billable hours: ${data.totalHours.toFixed(2)} hours across ${data.itemCount} tasks`,
        }));
      }
    } catch (err: any) {
      console.error('Failed to load unbilled hours:', err);
      setUnbilledHours(null);
    } finally {
      setLoadingUnbilled(false);
    }
  };

  const handleClientChange = async (client: Client | null) => {
    setSelectedClient(client);
    setFormData(prev => ({ ...prev, client_id: client?.id }));
    setUnbilledHours(null);
    
    if (client?.id) {
      await loadUnbilledHours(client.id);
    }
  };

  const handleCreateInvoice = () => {
    setSelectedInvoice(null);
    setSelectedClient(null);
    setUnbilledHours(null);
    setShowUnbilledDetails(false);
    setFormData({
      status: 'draft',
      issue_date: new Date().toISOString().split('T')[0],
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    if (!loading) {
      setOpenDialog(false);
      setUnbilledHours(null);
      setShowUnbilledDetails(false);
    }
  };

  const handleEditInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    const client = clients.find(c => c.id === invoice.clientId);
    setSelectedClient(client || null);
    setFormData({
      client_id: invoice.clientId,
      project_id: invoice.projectId,
      amount: invoice.amount,
      status: invoice.status,
      issue_date: invoice.issueDate,
      due_date: invoice.dueDate,
      paid_date: invoice.paidDate,
      description: invoice.description,
      notes: invoice.notes,
    });
    setOpenDialog(true);
  };

  const handleDeleteInvoice = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) {
      return;
    }
    try {
      await InvoicesService.deleteInvoice(id);
      setSuccess('Invoice deleted successfully');
      loadInvoices();
      loadStats();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete invoice');
    }
  };

  const handleSubmit = async () => {
    if (!formData.client_id || !formData.amount) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      if (selectedInvoice) {
        await InvoicesService.updateInvoice(selectedInvoice.id, formData);
        setSuccess('Invoice updated successfully');
      } else {
        await InvoicesService.createInvoice(formData as CreateInvoiceData);
        setSuccess('Invoice created successfully');
      }
      setOpenDialog(false);
      loadInvoices();
      loadStats();
    } catch (err: any) {
      setError(err?.response?.data?.message || `Failed to ${selectedInvoice ? 'update' : 'create'} invoice`);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Invoice Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <IconButton onClick={loadInvoices} disabled={loading}>
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateInvoice}
            disabled={loading}
          >
            Create Invoice
          </Button>
        </Box>
      </Box>

      {stats && (
        <Paper sx={{ mb: 3, p: 2 }}>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 200, textAlign: 'center' }}>
              <Typography variant="h6" color="primary">
                ${stats.outstandingAmount.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Outstanding
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 200, textAlign: 'center' }}>
              <Typography variant="h6" color="success.main">
                ${stats.monthlyRevenue.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Paid This Month
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 200, textAlign: 'center' }}>
              <Typography variant="h6" color="error.main">
                {stats.overdueInvoices}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Overdue Invoices
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 200, textAlign: 'center' }}>
              <Typography variant="h6">
                {stats.pendingInvoices}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Invoices
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Status</InputLabel>
          <Select
            value={statusFilter}
            label="Filter by Status"
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">All Invoices</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="sent">Sent</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="overdue">Overdue</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : invoices.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No invoices found. Create your first invoice to get started.
            </Typography>
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Balance Due</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Issue Date</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>{invoice.invoiceNumber}</TableCell>
                    <TableCell>{invoice.clientName || 'Unknown'}</TableCell>
                    <TableCell align="right">${invoice.amount.toLocaleString()}</TableCell>
                    <TableCell align="right">${invoice.balanceDue.toLocaleString()}</TableCell>
                    <TableCell>
                      <Chip
                        label={invoice.status.toUpperCase()}
                        color={getStatusColor(invoice.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{invoice.issueDate ? new Date(invoice.issueDate).toLocaleDateString() : '-'}</TableCell>
                    <TableCell>{invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : '-'}</TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleEditInvoice(invoice)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteInvoice(invoice.id)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 15, 25, 50]}
              component="div"
              count={totalInvoices}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedInvoice ? 'Edit Invoice' : 'Create New Invoice'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Autocomplete
              options={clients}
              getOptionLabel={(option) => option.name}
              value={selectedClient}
              onChange={(_event, newValue) => handleClientChange(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Client *"
                  required
                />
              )}
              disabled={loading}
            />

            {loadingUnbilled && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading unbilled hours...</Typography>
              </Box>
            )}

            {unbilledHours && unbilledHours.totalHours > 0 && (
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">
                    📊 Unbilled Hours Summary
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setShowUnbilledDetails(!showUnbilledDetails)}
                    endIcon={<ExpandMoreIcon sx={{ transform: showUnbilledDetails ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.3s' }} />}
                  >
                    {showUnbilledDetails ? 'Hide' : 'Show'} Details
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 3, mb: 1 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Total Hours</Typography>
                    <Typography variant="h6">{unbilledHours.totalHours.toFixed(2)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                    <Typography variant="h6">${unbilledHours.totalAmount.toLocaleString()}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Tasks</Typography>
                    <Typography variant="h6">{unbilledHours.itemCount}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Projects</Typography>
                    <Typography variant="h6">{unbilledHours.projectCount}</Typography>
                  </Box>
                </Box>
                
                {showUnbilledDetails && (
                  <Box sx={{ mt: 2, maxHeight: 300, overflow: 'auto' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Breakdown by Task:
                    </Typography>
                    {unbilledHours.unbilledItems.map((item, index) => (
                      <Paper key={index} sx={{ p: 1.5, mb: 1, bgcolor: 'background.paper' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" fontWeight="bold">
                            {item.taskTitle}
                          </Typography>
                          <Chip label={item.taskType} size="small" />
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {item.projectName}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                          <Typography variant="body2">
                            {item.hours.toFixed(2)} hrs × ${item.hourlyRate}/hr = <strong>${item.amount.toFixed(2)}</strong>
                          </Typography>
                        </Box>
                        {item.timeLogs.length > 0 && (
                          <Box sx={{ mt: 1, pl: 2, borderLeft: '2px solid #ddd' }}>
                            {item.timeLogs.map((log) => (
                              <Typography key={log.id} variant="caption" display="block" color="text.secondary">
                                • {log.user} - {log.hours} hrs ({log.date}) {log.description && `- ${log.description}`}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Paper>
                    ))}
                  </Box>
                )}
                
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  💡 Amount has been auto-populated based on unbilled hours
                </Typography>
              </Paper>
            )}

            {unbilledHours && unbilledHours.totalHours === 0 && (
              <Alert severity="info">
                No unbilled hours found for this client
              </Alert>
            )}
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Amount *"
                type="number"
                value={formData.amount || ''}
                onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })}
                required
                disabled={loading}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              />
              <FormControl fullWidth disabled={loading}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status || 'draft'}
                  label="Status"
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="sent">Sent</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Issue Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.issue_date || ''}
                onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                disabled={loading}
              />
              <TextField
                fullWidth
                label="Due Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.due_date || ''}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                disabled={loading}
              />
            </Box>

            {formData.status === 'paid' && (
              <TextField
                fullWidth
                label="Paid Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.paid_date || ''}
                onChange={(e) => setFormData({ ...formData, paid_date: e.target.value })}
                disabled={loading}
              />
            )}

            <TextField
              fullWidth
              label="Description"
              multiline
              rows={2}
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              disabled={loading}
            />

            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={2}
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              disabled={loading}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            disabled={loading || !formData.client_id || !formData.amount}
          >
            {loading ? <CircularProgress size={24} /> : (selectedInvoice ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InvoicesPage;